import type {
    CampaignReportWindow,
    ICampaignReportContentItem,
    ICampaignReportContentResponse,
    ICampaignReportCreatorItem,
    ICampaignReportCreatorsResponse,
    ICampaignReportSummary,
} from '../../../types/global/campaignReport.types';

export type CampaignReportPreviewScenario = 'full' | 'empty' | 'syncing';

export const CAMPAIGN_REPORT_CONTENT_PAGE_SIZE = 20;
export const CAMPAIGN_REPORT_CREATORS_PAGE_SIZE = 20;

export function isCampaignReportDevToolsEnabled(): boolean {
    const env = (process.env.REACT_APP_NODE_ENV || process.env.NODE_ENV || '').toLowerCase();
    return env === 'dev' || env === 'development';
}

const MOCK_CREATOR_NAMES = [
    'Alex Rivera',
    'Sarah Jenkins',
    'Jordan Smith',
    'Maya Chen',
    'Alex Rivers',
    'Priya Kapoor',
    'Marcus Lee',
    'Elena Voss',
    'Noah Williams',
    'Zara Okonkwo',
    'Chris Dalton',
    'Aisha Rahman',
    'Tyler Brooks',
    'Sofia Martinez',
    'Jamal Wright',
    'Hannah Park',
    'Diego Fernandez',
    'Chloe Nguyen',
    'Ryan OConnor',
    'Imani Carter',
    'Lucas Berger',
    'Nina Petrova',
    'Omar Hassan',
    'Grace Lindstrom',
];

const MOCK_PLATFORMS = [
    ['tiktok', 'instagram'],
    ['tiktok'],
    ['instagram'],
    ['youtube'],
    ['tiktok', 'youtube'],
    ['instagram', 'youtube'],
];

function buildMockCreator(rank: number): ICampaignReportCreatorItem {
    const name = MOCK_CREATOR_NAMES[(rank - 1) % MOCK_CREATOR_NAMES.length];
    const impact = Math.max(42, 96 - (rank - 1) * 2);
    const views = Math.max(180_000, 2_400_000 - (rank - 1) * 95_000);
    return {
        rank,
        influencer_id: `mock-inf-${rank}`,
        creator_name: name,
        platforms: MOCK_PLATFORMS[(rank - 1) % MOCK_PLATFORMS.length],
        posts: Math.max(3, 14 - Math.floor(rank / 3)),
        views,
        reach: Math.round(views * 0.78),
        engagements: Math.round(views * (0.06 + impact / 1000)),
        engagement_rate: Math.max(3.8, 8.4 - rank * 0.12),
        impact_score: impact,
        payout_amount_cents: Math.max(45_000, 120_000 - (rank - 1) * 2_500),
        participant_status: 'completed',
    };
}

function buildMockContentItem(index: number, scenario: CampaignReportPreviewScenario): ICampaignReportContentItem {
    const rank = index + 1;
    const creator = buildMockCreator(rank);
    const platform = creator.platforms[0];
    const views = Math.max(320_000, 1_200_000 - index * 85_000);
    return {
        id: `mock-content-${rank}`,
        submission_id: `mock-sub-${rank}`,
        participant_id: `mock-part-${rank}`,
        influencer_id: creator.influencer_id,
        creator_name: creator.creator_name,
        participant_status: 'completed',
        payout_amount_cents: creator.payout_amount_cents,
        platform,
        live_url: `https://${platform}.com/post/${rank}`,
        deliverable_type:
            platform === 'instagram' ? 'instagram_reel' : platform === 'tiktok' ? 'tiktok_video' : 'youtube_short',
        sync_status: scenario === 'syncing' && rank > 4 ? 'pending' : 'synced',
        last_synced_at: new Date().toISOString(),
        metrics_ready: !(scenario === 'syncing' && rank > 4),
        metrics: {
            views,
            reach: Math.round(views * 0.82),
            likes: Math.round(views * 0.05),
            comments: Math.round(views * 0.004),
            shares: Math.round(views * 0.006),
            saves: Math.round(views * 0.003),
            engagements: Math.round(views * 0.063),
            engagement_rate: Math.max(3.5, 7.2 - index * 0.35),
            impact_score: creator.impact_score,
        },
        impact_score: creator.impact_score,
    };
}

export function buildCampaignReportPreviewSummary(
    campaignId: string,
    campaignTitle: string,
    window: CampaignReportWindow,
    scenario: CampaignReportPreviewScenario,
): ICampaignReportSummary {
    const base: ICampaignReportSummary = {
        campaign: { id: campaignId, title: campaignTitle, total_budget_cents: 2_500_000 },
        window,
        health: {
            invited: 520,
            approved_participants: 467,
            in_progress: 12,
            completed: 455,
            content_submitted: 950,
            content_approved: 920,
            content_published: scenario === 'empty' ? 0 : 442,
            overdue: 3,
        },
        performance: {
            posts_tracked: scenario === 'empty' ? 0 : 912,
            views: 9_700_000,
            impressions: 8_200_000,
            reach: 6_600_000,
            likes: 420_000,
            comments: 38_000,
            shares: 52_000,
            saves: 19_000,
            engagements: 529_000,
            engagement_rate: 6.6,
            watch_time_seconds: 44_640,
        },
        platform_breakdown: {
            instagram: { posts: 312, views: 3_800_000, reach: 4_200_000, engagements: 210_000 },
            tiktok: { posts: 485, views: 5_100_000, engagements: 280_000 },
            youtube: { posts: 115, views: 400_000, engagements: 39_000 },
        },
        roi: {
            campaign_spend_usd: 25_000,
            cpm: 2.58,
            cpv: 1.02,
            cpe: 0.15,
            estimated_media_value_usd: 48_500,
        },
        sync: {
            total_tracked_urls: scenario === 'empty' ? 0 : 912,
            by_status:
                scenario === 'empty'
                    ? { pending: 0, synced: 0 }
                    : { pending: 142, synced: 712, failed: 38, unsupported: 20 },
            sync_progress_pct: scenario === 'syncing' ? 45 : scenario === 'empty' ? 0 : 78,
            last_updated_at: new Date().toISOString(),
        },
    };

    if (scenario === 'empty') {
        base.performance = {
            posts_tracked: 0,
            views: 0,
            impressions: 0,
            reach: 0,
            likes: 0,
            comments: 0,
            shares: 0,
            saves: 0,
            engagements: 0,
            engagement_rate: 0,
            watch_time_seconds: 0,
        };
        base.platform_breakdown = {};
        base.roi = {
            campaign_spend_usd: 25_000,
            cpm: null,
            cpv: null,
            cpe: null,
            estimated_media_value_usd: null,
        };
    }

    return base;
}

export function buildCampaignReportPreviewContent(
    scenario: CampaignReportPreviewScenario,
    page = 1,
    limit = CAMPAIGN_REPORT_CONTENT_PAGE_SIZE,
    platform?: string | null,
): ICampaignReportContentResponse {
    if (scenario === 'empty') {
        return { items: [], pagination: { page: 1, limit, total: 0, has_more: false } };
    }

    const allItems = Array.from({ length: 15 }, (_, i) => buildMockContentItem(i, scenario));
    const filtered = platform
        ? allItems.filter(item => String(item.platform).toLowerCase() === platform.toLowerCase())
        : allItems;
    const total = filtered.length;
    const start = (page - 1) * limit;
    const items = filtered.slice(start, start + limit);

    return {
        items,
        pagination: {
            page,
            limit,
            total,
            has_more: start + limit < total,
        },
    };
}

export function buildCampaignReportPreviewCreators(
    scenario: CampaignReportPreviewScenario,
    page = 1,
    limit = CAMPAIGN_REPORT_CREATORS_PAGE_SIZE,
): ICampaignReportCreatorsResponse {
    if (scenario === 'empty') {
        return { creators: [], pagination: { page: 1, limit, total: 0, has_more: false } };
    }

    const allCreators = Array.from({ length: 24 }, (_, i) => buildMockCreator(i + 1));
    const total = allCreators.length;
    const start = (page - 1) * limit;
    const creators = allCreators.slice(start, start + limit);

    return {
        creators,
        pagination: {
            page,
            limit,
            total,
            has_more: start + limit < total,
        },
    };
}

export function getInitialCampaignReportDataMode(): 'live' | 'preview' {
    return isCampaignReportDevToolsEnabled() ? 'preview' : 'live';
}
